import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { transformFormValuesToRecurrenceDto } from 'src/Hooks/Forms/UseCreateEventForm/transformFormValuesToRecurrenceDto';
import { CreateEventFormValues } from 'src/Hooks/Forms/UseCreateEventForm/useCreateEventForm.types';
import {
  CreateEventDto,
  RecurrenceDtoFrequencyEnum,
  EventDtoTypeEnum,
} from 'src/Services/API/generated';

export const defaultRecurrenceValue = {
  frequency: undefined,
  customFrequency: RecurrenceDtoFrequencyEnum.Daily,
  interval: '1',
  byMonthDays: [],
  byMonth: [],
  monthDaySelected: false,
  monthDayNumberOrAdvanced: 'number' as 'number' | 'advanced',
  byWeekDay: '',
  byWeekDays: [],
  byMonthDay: '1',
  reminderDurationSeconds: undefined,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  until: undefined,
};

interface UseCreateEventFormOptions {
  isReservation?: boolean;
  defaultDate?: number;
  defaultHouseholdId?: string;
}

const useCreateEventForm = (options?: UseCreateEventFormOptions) => {
  const { t } = useTranslation();

  const defaultType = EventDtoTypeEnum.Occupation;

  const startTime = options?.defaultDate ? new Date(options.defaultDate) : new Date();
  const endTime = options?.defaultDate
    ? new Date(options.defaultDate + 60 * 60 * 1000)
    : dayjs().add(1, 'hour').toDate();

  return useForm<CreateEventFormValues, (values: CreateEventFormValues) => CreateEventDto>({
    mode: 'controlled',
    initialValues: {
      name: '',
      type: defaultType,
      description: '',
      placeId: '',
      videoConferenceUrl: '',
      startTime,
      endTime,
      isExclusiveOccupation: false,
      recurrence: defaultRecurrenceValue,
      participantIds: [],
      householdIds: options?.defaultHouseholdId ? [options.defaultHouseholdId] : [],
      roomIds: [],
    },
    validate: {
      name: (value: string) =>
        value.length < 2 ? t('createEventForm.errors.invalidName', 'Nom invalide') : null,
      type: (value: EventDtoTypeEnum) =>
        !Object.values(EventDtoTypeEnum).includes(value)
          ? t('createEventForm.errors.invalidType', 'Type invalide')
          : null,
      startTime: (value: Date, values) => {
        const startTime = dayjs(value);
        const endTime = dayjs(values.endTime);

        return startTime.isBefore(endTime)
          ? null
          : t(
              'createEventForm.errors.startTimeisAfterEnd',
              'La date de début doit être antérieure à la date de fin.'
            );
      },
      recurrence: {
        interval: (value, values) => {
          if (values.recurrence.frequency === 'CUSTOM') {
            return value
              ? null
              : t('createEventForm.errors.missingInterval', "L'interval est manquant");
          }
        },
      },
    },
    transformValues: values => ({
      ...values,
      startTime: dayjs(values.startTime),
      endTime: dayjs(values.endTime),
      reminderDurationSeconds:
        values.reminderDurationSeconds !== undefined && values.reminderDurationSeconds !== null
          ? Number(values.reminderDurationSeconds)
          : values.reminderDurationSeconds,
      recurrence: transformFormValuesToRecurrenceDto(values.recurrence),
    }),
  });
};
export default useCreateEventForm;
